// The extension registry. See spec/02-extensibility/extensibility.md.
//
// Extensions live in src/extensions/<module>/<name>.vue and register themselves
// by exporting an `extension` selector next to the component. Nothing imports
// them: webpack's require.context builds the index, so dropping a file in is the
// whole registration step.

import { globMatchList } from './glob'

export type ExtensionCtx = {
	spaceId?: string
	environment?: string
	space?: any
	contentType?: any
	entry?: any
	field?: any
	control?: any
	locale?: string
	locales?: any[]
	defaultLocale?: string
	uiConfig?: any
	permissions?: any
	settings?: any
	me?: any
	tags?: any[]
	eventBus?: any
	base?: string
	endpoint?: string
}

export type ExtensionSelector = {
	/** which family of hook, e.g. 'entryEditor' | 'fieldEditor' | 'contentList' | 'mainMenu' */
	type: string
	/** which hook within the family, e.g. 'top' | 'bottom' | 'toolbar' */
	location: string
	/** sort order within one slot, default 0 */
	order?: number
	spaceIds?: string[]
	environments?: string[]
	contentTypes?: string[]
	fieldIds?: string[]
	widgetIds?: string[]
	/** dot-paths resolved against uiConfig.additional; truthy = match */
	uiConfigFlags?: string[]
	/** ANDed with the declarative filters, for anything they cannot express */
	when?: (ctx: ExtensionCtx) => boolean
	/** defaults to the default export of the .vue file */
	component?: any
}

export type Extension = ExtensionSelector & {
	component: any
	/** the folder name under src/extensions - a grouping label, nothing more */
	module: string
	/** `<module>/<name>`, stable across builds; used for sorting and logging */
	id: string
}

// Each declarative filter maps a selector key to the ctx value it constrains.
const FILTERS: { [ key: string ]: (ctx: ExtensionCtx) => string | null | undefined } = {
	spaceIds: ctx => ctx.spaceId,
	environments: ctx => ctx.environment,
	// accepts either a content type object or a plain id
	contentTypes: ctx => ctx.contentType?.sys?.id ?? ctx.contentType,
	fieldIds: ctx => ctx.field?.id,
	widgetIds: ctx => ctx.control?.widgetId,
}

const KNOWN_KEYS = [ 'type', 'location', 'order', 'uiConfigFlags', 'when', 'component', ...Object.keys(FILTERS) ]

// ATT: a literal directory is required - webpack resolves require.context at
//      build time, so this cannot be a variable.
const context = require.context('../../extensions', true, /\.vue$/)

function buildRegistry(): Extension[] {
	const extensions: Extension[] = []
	for (const key of context.keys()) {
		const mod = context(key)

		// No `extension` export means this is a helper component of some extension
		// rather than an extension itself. That is the only rule - there is no
		// filename convention that could disagree with the code.
		if (!mod.extension) continue

		const [ , module = '', name = key ] = key.match(/^\.\/(?:(.+)\/)?([^/]+)\.vue$/) ?? []
		if (!module)
			console.warn(`[extensions] ${key} is not inside a module folder - see src/extensions/README.md`)

		const defs: ExtensionSelector[] = [].concat(mod.extension)
		defs.forEach((def, i) => {
			const id = (module ? module + '/' : '') + name + (defs.length > 1 ? '#' + i : '')
			const component = def.component ?? mod.default

			if (!def.type || !def.location) {
				console.error(`[extensions] ${id} has no type/location and is ignored`, def)
				return
			}
			if (!component) {
				console.error(`[extensions] ${id} has no component and is ignored`)
				return
			}
			for (const k of Object.keys(def)) {
				// A typo'd key would otherwise read as "matches everything", because an
				// absent filter is no constraint. Loud, not silent.
				if (!KNOWN_KEYS.includes(k))
					console.error(`[extensions] ${id}: unknown selector key '${k}' - it does NOT filter anything`)
			}
			for (const k of Object.keys(FILTERS)) {
				if (def[k] && !def[k].length)
					console.warn(`[extensions] ${id}: '${k}' is an empty list and is ignored`)
			}

			extensions.push({ ...def, component, module, id })
		})
	}

	// stable and independent of the order require.context happens to yield
	return extensions.sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id.localeCompare(b.id))
}

let registry: Extension[] | null = null

export function allExtensions(): Extension[] {
	if (!registry) registry = buildRegistry()
	return registry
}

// Adding, removing or re-selecting an extension changes the require.context
// dependency list, which HMR cannot patch into an already-built registry - the
// change simply does not appear until the page is reloaded. Do that explicitly
// rather than leaving it looking like the extension is broken.
if (module.hot) {
	module.hot.accept(context.id, () => location.reload())
}

function uiConfigFlag(ctx: ExtensionCtx, path: string) {
	return path.split('.').reduce((o, k) => o?.[k], ctx.uiConfig?.additional)
}

/** why an extension did not match, or null if it did. */
export function reject(ext: Extension, ctx: ExtensionCtx): string | null {
	for (const key of Object.keys(FILTERS)) {
		if (!ext[key]) continue
		const value = FILTERS[key](ctx)
		if (!globMatchList(ext[key], value)) return `${key} does not match '${value}'`
	}
	for (const path of ext.uiConfigFlags ?? []) {
		if (!uiConfigFlag(ctx, path)) return `uiConfig.additional.${path} is not set`
	}
	if (ext.when) {
		try {
			if (!ext.when(ctx)) return 'when() returned false'
		}
		catch (e) {
			// a throwing predicate must not take the host down with it
			console.error(`[extensions] ${ext.id}: when() threw`, e)
			return 'when() threw'
		}
	}
	return null
}

export function matches(ext: Extension, ctx: ExtensionCtx): boolean {
	return reject(ext, ctx) === null
}

/** the extensions to render in one slot, in order */
export function getExtensions(type: string, location: string, ctx: ExtensionCtx): Extension[] {
	return allExtensions().filter(e => e.type === type && e.location === location && matches(e, ctx))
}

// Debugging aid: `__extensions.explain('contentList', 'toolbar', ctx)` answers
// "why is my extension not showing", which is otherwise pure guesswork.
;(window as any).__extensions = {
	get all() { return allExtensions() },
	explain(type: string, location: string, ctx: ExtensionCtx) {
		return allExtensions()
			.filter(e => e.type === type && e.location === location)
			.map(e => ({ id: e.id, rejectedBy: reject(e, ctx) }))
	},
}
